// MoleculePlayFloating.jsx
import React from "react";
import styled, { css } from "styled-components";

/** ----------------------------------------------------------------
 *  API
 *  <MoleculePlayFloating
 *     isPlaying={bool}                 // 선택: 외부 제어(컨트롤드)
 *     onTogglePlay={(next)=>{}}
 *     onPrev={()=>{}}
 *     onNext={()=>{}}
 *     prevLabel="이전"
 *     nextLabel="다음"
 *     floating={true}                  // true면 화면 하단 중앙 플로팅
 *     zIndex={9999}
 *  />
 *  ---------------------------------------------------------------- */

const MoleculePlayFloating = ({
  isPlaying: controlledPlaying,
  onTogglePlay,
  onPrev,
  onNext,
  prevLabel = "이전",
  nextLabel = "다음",
  floating = true,
  zIndex = 9999,
  className,
  style,
}) => {
  // 언컨트롤드용 내부 상태
  const [internalPlaying, setInternalPlaying] = React.useState(true);
  const playing = controlledPlaying ?? internalPlaying;

  const handleToggle = () => {
    if (onTogglePlay) onTogglePlay(!playing);
    if (controlledPlaying === undefined) setInternalPlaying((p) => !p);
  };

  return (
    <Wrap
      className={className}
      style={style}
      floating={floating}
      zIndex={zIndex}
      role="group"
      aria-label="슬라이드 컨트롤러"
    >
      {/* 이전 */}
      <SideGroup>
        <ArrowLeftSVG aria-hidden />
        <SideLabel>{prevLabel}</SideLabel>
        <SideButton
          type="button"
          onClick={onPrev}
          aria-label="이전 슬라이드로 이동"
          title="이전"
        />
      </SideGroup>

      {/* 재생/일시정지 토글 */}
      <CenterPill>
        <CenterButton
          type="button"
          onClick={handleToggle}
          aria-pressed={!playing}
          aria-label={playing ? "일시정지" : "재생"}
          title={playing ? "일시정지" : "재생"}
        >
          {playing ? <PauseSVG /> : <PlaySVG />}
        </CenterButton>
      </CenterPill>

      {/* 다음 */}
      <SideGroup>
        <SideLabel>{nextLabel}</SideLabel>
        <ArrowRightSVG aria-hidden />
        <SideButton
          type="button"
          onClick={onNext}
          aria-label="다음 슬라이드로 이동"
          title="다음"
        />
      </SideGroup>
    </Wrap>
  );
};

export default MoleculePlayFloating;

/* ===================== Icons (as SVG) ===================== */

const PauseSVG = (props) => (
  <svg
    width="28"
    height="33"
    viewBox="0 0 28 33"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    {...props}
  >
    <path
      d="M8 0.015625H4C2.93913 0.015625 1.92172 0.437052 1.17157 1.1872C0.421427 1.93734 0 2.95476 0 4.01562V28.0156C0 29.0765 0.421427 30.0939 1.17157 30.8441C1.92172 31.5942 2.93913 32.0156 4 32.0156H8C9.06087 32.0156 10.0783 31.5942 10.8284 30.8441C11.5786 30.0939 12 29.0765 12 28.0156V4.01562C12 2.95476 11.5786 1.93734 10.8284 1.1872C10.0783 0.437052 9.06087 0.015625 8 0.015625ZM24 0.015625H20C18.9391 0.015625 17.9217 0.437052 17.1716 1.1872C16.4214 1.93734 16 2.95476 16 4.01562V28.0156C16 29.0765 16.4214 30.0939 17.1716 30.8441C17.9217 31.5942 18.9391 32.0156 20 32.0156H24C25.0609 32.0156 26.0783 31.5942 26.8284 30.8441C27.5786 30.0939 28 29.0765 28 28.0156V4.01562C28 2.95476 27.5786 1.93734 26.8284 1.1872C26.0783 0.437052 25.0609 0.015625 24 0.015625Z"
      fill="#226FFF"
    />
  </svg>
);

const PlaySVG = (props) => (
  <svg
    width="28"
    height="32"
    viewBox="0 0 28 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.4 30.6769L25.2 20.9777C28.9333 18.8217 28.9333 13.4345 25.2 11.2785L8.4 1.57933C4.66667 -0.576673 -4.46918e-07 2.11693 -4.46918e-07 6.42893V25.8273C-4.46918e-07 30.1393 4.66667 32.8329 8.4 30.6769Z"
      fill="#226FFF"
    />
  </svg>
);

const ArrowLeftSVG = (props) => (
  <svg
    width="21"
    height="38"
    viewBox="0 0 21 38"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M19 1.75L1.75 19L19 36.25"
      stroke="#323232"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightSVG = (props) => (
  <svg
    width="21"
    height="38"
    viewBox="0 0 21 38"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M2 1.75L19.25 19L2 36.25"
      stroke="#323232"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ===================== styled ===================== */

const Wrap = styled.div`
  padding: 16px 56px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0px 18px 48px rgba(0, 0, 0, 0.16);
  border-radius: 58px;
  display: inline-flex;
  align-items: center;
  gap: 36px;

  ${(p) =>
    p.floating &&
    css`
      position: fixed;
      left: 50%;
      transform: translateX(-50%);
      bottom: 64px;
      z-index: ${p.zIndex};
    `}
`;

const SideGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
`;

/* 클릭 영역(투명 버튼) – 아이콘과 라벨을 한 번에 누를 수 있게 뒤에 깔아둡니다 */
const SideButton = styled.button`
  position: absolute;
  inset: -12px -16px;
  background: transparent;
  border: 0;
  cursor: pointer;
`;

const SideLabel = styled.span`
  color: #323232;
  font-size: 24px;
  font-family: Pretendard, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  font-weight: 600;
  line-height: 42px;
  user-select: none;
`;

/* 가운데 캡슐(연파랑 배경) */
const CenterPill = styled.div`
  background: #ecf2fe;
  border-radius: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CenterButton = styled.button`
  width: 84px;
  height: 84px;
  display: flex;           /* SVG 중앙 정렬 */
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;

  &:focus-visible {
    outline: 4px solid rgba(34, 111, 255, 0.35);
    outline-offset: 2px;
  }
`;
